// src/components/quickMessage/ThreadStrip.tsx -- prior-message strip that
// appears behind the clock icon on an expanded banner.
//
// The strip is read-only. It shows up to 3 prior messages between the
// current user and the other party in oldest-first order. A loading or
// error placeholder occupies the same vertical space while the GET is
// in flight, to avoid layout shift.

import type { CSSProperties } from "react";
import { C, F } from "../../theme";
import {
  historyEyebrowSuffix,
  type ThreadMessage,
} from "../../lib/quickMessages";

export interface ThreadStripProps {
  messages: ThreadMessage[] | null;
  loading: boolean;
  error: string | null;
  /** Short name for the other party in the thread (e.g. "Margaret W."). Used
   * as the "received" direction prefix. The thread endpoint doesn't return
   * sender info per message -- the Banner anchor's sender is the only other
   * party in the conversation, so passing it down is exact. */
  otherPartyName: string;
}

const stripStyle: CSSProperties = {
  background: C.paperWarm,
  borderRadius: 10,
  padding: "10px 12px",
  marginBottom: 12,
  border: `1px dashed ${C.hairline}`,
};

const eyebrowStyle: CSSProperties = {
  fontFamily: F.body,
  fontSize: 9.5,
  fontWeight: 700,
  letterSpacing: 1.2,
  textTransform: "uppercase",
  color: C.inkMute,
  marginBottom: 8,
  display: "flex",
  alignItems: "center",
  gap: 6,
};

export function ThreadStrip({ messages, loading, error, otherPartyName }: ThreadStripProps) {
  if (loading) {
    return (
      <div style={stripStyle}>
        <div style={eyebrowStyle}>
          <ClockGlyph /> Loading earlier messages…
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div style={stripStyle}>
        <div style={{ ...eyebrowStyle, color: C.signalDk }}>
          <ClockGlyph /> Couldn&rsquo;t load earlier messages
        </div>
      </div>
    );
  }
  if (!messages || messages.length === 0) {
    // Defensive: thread endpoint returned an empty list. Don't render the
    // strip at all -- the clock icon should already have been hidden upstream
    // when threadCount === 0. This is a no-op fallback.
    return null;
  }

  const suffix = historyEyebrowSuffix(messages);

  return (
    <div style={stripStyle}>
      <div style={eyebrowStyle}>
        <ClockGlyph /> Earlier · {suffix}
      </div>
      {messages.map((m, i) => (
        <ThreadRow
          key={m.id}
          message={m}
          isLast={i === messages.length - 1}
          otherPartyName={otherPartyName}
        />
      ))}
    </div>
  );
}

function ThreadRow({
  message,
  isLast,
  otherPartyName,
}: {
  message: ThreadMessage;
  isLast: boolean;
  otherPartyName: string;
}) {
  const sent = message.direction === "sent";
  const time = formatHM(new Date(message.createdAt));
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginBottom: isLast ? 0 : 6,
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          marginTop: 6,
          flexShrink: 0,
          background: sent ? C.brassLt : "#A8C3DD",
        }}
        aria-hidden="true"
      />
      <div
        style={{
          flex: 1,
          fontFamily: F.serif,
          fontStyle: "italic",
          fontSize: 12,
          color: C.inkSoft,
          lineHeight: 1.4,
        }}
      >
        <strong
          style={{
            fontStyle: "normal",
            color: C.ink,
            fontWeight: 700,
            fontSize: 11,
            letterSpacing: 0.2,
            marginRight: 4,
          }}
        >
          {sent ? "You ·" : `${otherPartyName} ·`}
        </strong>
        {message.body}
        <span
          style={{
            fontFamily: F.mono,
            fontSize: 9.5,
            color: C.inkMute,
            letterSpacing: 0.4,
            marginLeft: 6,
            fontStyle: "normal",
          }}
        >
          {time}
        </span>
      </div>
    </div>
  );
}

function ClockGlyph() {
  return (
    <svg
      width={10}
      height={10}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.4}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block" }}
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function formatHM(d: Date): string {
  const h = String(d.getHours()).padStart(2, "0");
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}
